'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { FEATURES } from '@/lib/features';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  telephone: string;
  password: string;
  confirmPassword: string;
  farmName: string;
  county: string;
  accountType: string;
  cphNumber: string;
  agreeTerms: boolean;
  agreeDataProcessing: boolean;
  marketingOptIn: boolean;
}

const ukCounties = [
  'Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire',
  'Cheshire', 'Cornwall', 'Cumbria', 'Derbyshire', 'Devon', 'Dorset',
  'Durham', 'East Riding of Yorkshire', 'East Sussex', 'Essex', 'Gloucestershire',
  'Greater Manchester', 'Hampshire', 'Herefordshire', 'Hertfordshire',
  'Isle of Wight', 'Kent', 'Lancashire', 'Leicestershire', 'Lincolnshire',
  'London', 'Merseyside', 'Norfolk', 'North Yorkshire', 'Northamptonshire',
  'Northumberland', 'Nottinghamshire', 'Oxfordshire', 'Shropshire', 'Somerset',
  'South Yorkshire', 'Staffordshire', 'Suffolk', 'Surrey', 'Tyne and Wear',
  'Warwickshire', 'West Midlands', 'West Sussex', 'West Yorkshire', 'Wiltshire',
  'Worcestershire', 'Scotland', 'Wales', 'Northern Ireland',
];

const accountTypes = ['Farmer', 'Haulier', 'Dealer', 'Merchant', 'Auctioneer'];

// Map form account types to DB business_type enum values
const accountTypeMap: Record<string, string> = {
  Farmer: 'Farm',
  Haulier: 'Haulier',
  Dealer: 'Livestock Agent',
  Merchant: 'Supplier',
  Auctioneer: 'Auction Mart',
};

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [registered, setRegistered] = useState(false);
  const { signUp } = useAuth();

  const { register, handleSubmit, watch, trigger, formState: { errors } } = useForm<RegisterFormData>();
  const password = watch('password');

  const handleStep1Next = async () => {
    const valid = await trigger(['firstName', 'lastName', 'email', 'telephone', 'password', 'confirmPassword']);
    if (valid) setStep(2);
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
        businessName: data.farmName,
        businessType: accountTypeMap[data.accountType] || data.accountType,
        phone: data.telephone,
        county: data.county,
        cphNumber: data.cphNumber || '',
      });
      setRegistered(true);
      toast.success('Account created! Please check your email to verify your address.');
    } catch (err: any) {
      toast.error(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="flex flex-col items-center gap-5 py-6 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <Icon name="EnvelopeIcon" size={32} className="text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-foreground mb-2">Check your email</h2>
          <p className="text-muted-foreground text-sm max-w-xs mx-auto">
            We&apos;ve sent a verification link to your email address. Please click the link to activate your account before signing in.
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left w-full">
          <div className="flex items-start gap-2">
            <Icon name="InformationCircleIcon" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800 leading-relaxed">
              <strong>Didn&apos;t receive the email?</strong> Check your spam folder. If you still can&apos;t find it, contact{' '}
              <a href="mailto:contact@farmtradedirect.com" className="underline font-medium">contact@farmtradedirect.com</a>.
            </p>
          </div>
        </div>
        <button onClick={onSwitchToLogin} className="btn-primary w-full py-3">
          <Icon name="ArrowRightOnRectangleIcon" size={18} />
          Go to Sign In
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-extrabold text-foreground mb-1">Create your account</h2>
      <p className="text-muted-foreground text-sm mb-6">Free to join. List livestock, machinery and deadstock commission free.</p>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2].map((s) => (
          <React.Fragment key={`step-${s}`}>
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
              s <= step ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
            }`}>
              {s < step ? <Icon name="CheckIcon" size={14} /> : s}
            </div>
            {s < 2 && <div className={`flex-1 h-0.5 transition-all ${s < step ? 'bg-primary' : 'bg-muted'}`} />}
          </React.Fragment>
        ))}
        <span className="text-xs text-muted-foreground ml-2">Step {step} of 2</span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {step === 1 && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">First name</label>
                <input
                  type="text"
                  className={`form-input ${errors.firstName ? 'error' : ''}`}
                  placeholder="James"
                  {...register('firstName', { required: 'Required' })}
                />
                {errors.firstName && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="form-label">Last name</label>
                <input
                  type="text"
                  className={`form-input ${errors.lastName ? 'error' : ''}`}
                  placeholder="Whitfield"
                  {...register('lastName', { required: 'Required' })}
                />
                {errors.lastName && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label className="form-label">Email address</label>
              <input
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="you@yourfarm.co.uk"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.email.message}</p>}
            </div>

            <div>
              <label className="form-label">Telephone number</label>
              <p className="form-helper">Used to verify your account and for important notifications only</p>
              <input
                type="tel"
                className={`form-input mt-1 ${errors.telephone ? 'error' : ''}`}
                placeholder="+44 7700 900000"
                {...register('telephone', {
                  required: 'Telephone number is required',
                  pattern: { value: /^[\+]?[\d\s\-\(\)]{7,15}$/, message: 'Enter a valid telephone number' },
                })}
              />
              {errors.telephone && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.telephone.message}</p>}
            </div>

            <div>
              <label className="form-label">Password</label>
              <p className="form-helper">Minimum 8 characters, including one uppercase and one number</p>
              <div className="relative mt-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
                  placeholder="Create a strong password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                    pattern: { value: /(?=.*[A-Z])(?=.*[0-9])/, message: 'Must include uppercase and number' },
                  })}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                </button>
              </div>
              {errors.password && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.password.message}</p>}
            </div>

            <div>
              <label className="form-label">Confirm password</label>
              <input
                type="password"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="Repeat your password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: v => v === password || 'Passwords do not match',
                })}
              />
              {errors.confirmPassword && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.confirmPassword.message}</p>}
            </div>

            <button type="button" onClick={handleStep1Next} className="btn-primary w-full py-3 mt-1">
              Continue to Farm Details
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            <div>
              <label className="form-label">Account type</label>
              <p className="form-helper">Select the option that best describes you</p>
              <select
                className={`form-input mt-1 ${errors.accountType ? 'error' : ''}`}
                {...register('accountType', { required: 'Select an account type' })}
              >
                <option value="">Select account type…</option>
                {accountTypes.map((t) => <option key={`actype-${t}`} value={t}>{t}</option>)}
              </select>
              {errors.accountType && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.accountType.message}</p>}
            </div>

            <div>
              <label className="form-label">Farm / Business name</label>
              <input
                type="text"
                className={`form-input ${errors.farmName ? 'error' : ''}`}
                placeholder="Whitfield Arable Farm"
                {...register('farmName', { required: 'Farm or business name is required' })}
              />
              {errors.farmName && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.farmName.message}</p>}
            </div>

            <div>
              <label className="form-label">County</label>
              <select
                className={`form-input ${errors.county ? 'error' : ''}`}
                {...register('county', { required: 'Select your county' })}
              >
                <option value="">Select county…</option>
                {ukCounties.map((c) => <option key={`county-${c}`} value={c}>{c}</option>)}
              </select>
              {errors.county && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.county.message}</p>}
            </div>

            {/* Held back until CPH/APHA verification is actually in place —
                see FEATURES.cphVerification in src/lib/features.ts */}
            {FEATURES.cphVerification && (
              <div>
                <label className="form-label">CPH Number <span className="text-muted-foreground font-normal">(optional)</span></label>
                <p className="form-helper">County Parish Holding number — adds Verified badge to your profile</p>
                <input
                  type="text"
                  className="form-input mt-1"
                  placeholder="e.g. 12/345/6789"
                  {...register('cphNumber')}
                />
              </div>
            )}

            <div className="flex flex-col gap-3 bg-secondary rounded-xl p-4 border border-border">
              {/* GDPR Data Usage Notice */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Icon name="InformationCircleIcon" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>How we use your data:</strong> Your name, email, and telephone number are collected to create and manage your FarmTrade Direct account, verify your identity, and send service-related communications. We do not sell your personal data to third parties. You may withdraw consent at any time by contacting{' '}
                  <a href="mailto:contact@farmtradedirect.com" className="underline font-medium">contact@farmtradedirect.com</a>.
                </p>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-5 h-5 mt-0.5 accent-primary shrink-0" {...register('agreeTerms', { required: 'You must agree to the terms' })} />
                <span className="text-sm text-foreground">
                  I agree to the{' '}
                  {/* New tab on purpose: registration is multi-step client state,
                      so navigating away mid-form would throw away everything
                      they have already typed. */}
                  <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">Privacy Policy</Link>
                </span>
              </label>
              {errors.agreeTerms && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.agreeTerms.message}</p>}

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-5 h-5 mt-0.5 accent-primary shrink-0" {...register('agreeDataProcessing', { required: 'You must consent to data processing to create an account' })} />
                <span className="text-sm text-foreground">
                  I consent to FarmTrade Direct processing my personal data (including email and telephone number) to provide the service, as described in the{' '}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">Privacy Policy</Link>
                  {' '}(required under UK GDPR)
                </span>
              </label>
              {errors.agreeDataProcessing && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.agreeDataProcessing.message}</p>}

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-5 h-5 mt-0.5 accent-primary shrink-0" {...register('marketingOptIn')} />
                <span className="text-sm text-muted-foreground">
                  I would like to receive deals, farming offers and platform updates by email (optional — you can unsubscribe at any time)
                </span>
              </label>

              <p className="text-xs text-muted-foreground border-t border-border pt-3 mt-1">
                FarmTrade Direct Ltd is the data controller for your personal information. For full details on your rights (access, rectification, erasure, portability) see our{' '}
                <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">Privacy Policy</Link>{' '}
                or email{' '}
                <a href="mailto:contact@farmtradedirect.com" className="text-primary hover:underline font-medium">contact@farmtradedirect.com</a>.
              </p>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn-outline flex-1 py-3">
                <Icon name="ArrowLeftIcon" size={16} />
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex-[2] py-3 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ minHeight: 48 }}
              >
                {isLoading ? (
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                ) : (
                  <>
                    <Icon name="CheckCircleIcon" size={18} />
                    Create Free Account
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">
          Sign in here
        </button>
      </p>
    </div>
  );
}